import {
  AppliedFilters,
  Button,
  Container,
  Table,
  useModal,
} from "@fluidattacks/design";
import type { Row } from "@tanstack/react-table";
import _ from "lodash";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { ActionButtons } from "./action-buttons";
import { useVulnerabilityDraftsFilters } from "./filters";
import { useVulnerabilityDraftsQuery } from "./hooks";
import { getColumns } from "./utils";

import type { IOrganizationGroups } from "@types";
import { CVSS3Toggle } from "components/cvss3-toggle";
import { usePermissions } from "context/authz/hooks/use-permissions";
import { SubmittedForm } from "features/vulnerabilities/handle-acceptance-modal/submitted-form";
import type { IVulnRowAttr } from "features/vulnerabilities/types";
import { formatVulnerabilitiesTreatment } from "features/vulnerabilities/utils";
import { VulnerabilityModal } from "features/vulnerabilities/vulnerability-modal";
import { VulnerabilityState } from "gql/graphql";
import type { GetMeVulnerabilityDraftsQueryVariables } from "gql/graphql";
import { useSettingsStore } from "hooks/use-settings-store";
import { useTable } from "hooks/use-table";

const DEBOUNCE_DELAY_MS = 1500;
const MAX_QUERY_ITEMS = 20000;

const VulnerabilityDrafts: FC = (): JSX.Element => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { cvssVersion } = useSettingsStore();
  const { vulnerabilityId } = useParams() as { vulnerabilityId?: string };
  const [search, setSearch] = useState("");
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const navigate = useNavigate();
  const submittedFormRef = useModal("submitted-form");
  const { organizations } = usePermissions();
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const [filters, setFilters] =
    useState<GetMeVulnerabilityDraftsQueryVariables>({
      state: VulnerabilityState.Submitted,
    });

  const { Filters, options, removeFilter } = useVulnerabilityDraftsFilters({
    setFilters,
  });

  const {
    data: meVulnerabilityDraftsData,
    refetch: refetchVulnerabilityDrafts,
    fetchMore: fethMoreVulnerabilityDrafts,
    loading,
  } = useVulnerabilityDraftsQuery({ filters });

  const { total, pageInfo, edges } =
    meVulnerabilityDraftsData?.me.vulnerabilityDrafts ?? {};

  const vulnerabilities = useMemo(
    (): IVulnRowAttr[] =>
      formatVulnerabilitiesTreatment({
        organizationsGroups: organizations as unknown as IOrganizationGroups[],
        vulnerabilities: _.isUndefined(edges)
          ? []
          : edges.map((edge): IVulnRowAttr => edge.node),
      }),
    [edges, organizations],
  );

  useEffect((): void => {
    const fetchAllVulnerabilityData = async (): Promise<void> => {
      if (!meVulnerabilityDraftsData?.me.vulnerabilityDrafts || isLoadingAll)
        return;

      if (
        total === 0 ||
        pageInfo?.hasNextPage === false ||
        (edges?.length ?? 0) > MAX_QUERY_ITEMS
      )
        return;

      setIsLoadingAll(true);

      await fethMoreVulnerabilityDrafts({
        variables: { after: pageInfo?.endCursor, first: 200 },
      });

      setIsLoadingAll(false);
    };

    void fetchAllVulnerabilityData();
  }, [
    meVulnerabilityDraftsData,
    fethMoreVulnerabilityDrafts,
    isLoadingAll,
    total,
    pageInfo,
    edges,
  ]);

  const toggleConfirmModal = useCallback((state: boolean): VoidFunction => {
    return (): void => {
      setIsConfirmModalOpen(state);
    };
  }, []);
  const refreshDrafts = useCallback(async (): Promise<void> => {
    await refetchVulnerabilityDrafts();
  }, [refetchVulnerabilityDrafts]);
  const onRowClick = useCallback(
    (row: Row<IVulnRowAttr>): void => {
      navigate(`${pathname}/${row.original.id}`, { replace: true });
    },
    [navigate, pathname],
  );

  const formattedVulns = useMemo(
    (): IVulnRowAttr[] =>
      vulnerabilities.filter(
        ({ groupName, finding, where }): boolean =>
          where.toLowerCase().includes(search.toLowerCase()) ||
          groupName.toLowerCase().includes(search.toLowerCase()) ||
          (finding?.title.toLowerCase().includes(search.toLowerCase()) ??
            false),
      ),
    [vulnerabilities, search],
  );

  const { table } = useTable({
    columns: getColumns({
      accessorKey: cvssVersion.scoreKey,
      header: cvssVersion.header,
    }),
    data: formattedVulns,
    id: "vulns-drafts-table",
    options: {
      actionsOptions: { onSearch: setSearch },
      columnOptions: {
        hidden: ["organizationName", "reportDate"],
        order: [
          "where",
          "specific",
          "groupName",
          "finding-title",
          "state",
          "evidence",
          "severity",
          "hacker",
        ],
        pinned: ["where"],
      },
      filtersMode: "client-side",
      loading:
        loading ||
        isLoadingAll ||
        ((total ?? 0) > (edges?.length ?? 0) &&
          (edges?.length ?? 0) < MAX_QUERY_ITEMS),
      paginationMode: "client-side",
      rowOptions: { onClick: onRowClick },
    },
    total: formattedVulns.length,
  });

  const { rowSelection } = table.getState();
  const { data: tableData } = table.options.meta ?? {};
  const selectedVulns: IVulnRowAttr[] = Object.keys(rowSelection).map(
    (key): IVulnRowAttr => tableData?.get(key) ?? ({} as IVulnRowAttr),
  );

  return (
    <Fragment>
      <Table table={table}>
        <Table.Options>
          <Table.Controls>
            <Filters />
            <Table.Columns table={table} />
            <Container gap={0.5} id={"actions-container"}>
              <CVSS3Toggle />
              <Table.Export
                csvConfig={{
                  columns: [
                    "groupName",
                    "finding.title",
                    cvssVersion.scoreKey,
                    "findingId",
                    "hacker",
                    "organizationName",
                    "id",
                    "where",
                    "specific",
                    "state",
                    "reportDate",
                    "lastStateDate",
                  ],
                  export: true,
                  headers: {
                    "finding.title": t(
                      "searchFindings.tabVuln.vulnTable.vulnerabilityType.title",
                    ),
                    findingId: t(
                      "searchFindings.tabVuln.vulnTable.vulnerabilityId",
                    ),
                    groupName: t("organization.tabs.groups..text"),
                    hacker: t("searchFindings.tabVuln.vulnTable.hacker"),
                    id: t("searchFindings.tabVuln.vulnTable.locationId"),
                    lastStateDate: t(
                      "searchFindings.tabVuln.vulnTable.stateDate",
                    ),
                    organizationName: t(
                      "searchFindings.tabVuln.vulnTable.organization",
                    ),
                    reportDate: t(
                      "searchFindings.tabVuln.vulnTable.reportDate",
                    ),
                    severityThreatScore: t(
                      "searchFindings.tabDescription.severity",
                    ),
                    specific: t("searchFindings.tabVuln.vulnTable.specific"),
                    state: t("searchFindings.tabVuln.vulnTable.status"),
                    where: t("searchFindings.tabVuln.vulnTable.where"),
                  },
                }}
                data={formattedVulns}
              >
                <Button
                  icon={"file-export"}
                  id={"CSVExportBtn"}
                  tooltip={t("buttons.exportTip", { format: "CSV" })}
                  variant={"ghost"}
                >
                  {t("group.findings.exportCsv.text")}
                </Button>
              </Table.Export>
              <ActionButtons
                noVulnInSubmittedState={
                  vulnerabilities.filter(
                    (vuln): boolean => vuln.state === "SUBMITTED",
                  ).length === 0
                }
                openConfirmModal={toggleConfirmModal(true)}
                refreshDrafts={refreshDrafts}
              />
            </Container>
          </Table.Controls>
        </Table.Options>
        <Table.Caption>
          <AppliedFilters onClose={removeFilter} options={options} />
        </Table.Caption>
        <Table.Content />
        <Table.Footer />
      </Table>
      {vulnerabilityId !== undefined && vulnerabilityId !== "" && (
        <VulnerabilityModal
          cvssVersion={cvssVersion.scoreKey}
          findingId={selectedVulns[0]?.findingId ?? ""}
          groupName={selectedVulns[0]?.groupName ?? ""}
          vulnerabilityId={vulnerabilityId}
        />
      )}
      <SubmittedForm
        displayGlobalColumns={true}
        modalRef={{ ...submittedFormRef, isOpen: isConfirmModalOpen }}
        onCancel={toggleConfirmModal(false)}
        refetchData={_.debounce(refreshDrafts, DEBOUNCE_DELAY_MS)}
        vulnerabilities={vulnerabilities}
      />
    </Fragment>
  );
};

export { VulnerabilityDrafts };
